import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import { generateForm5291PDF, downloadPDF5291, type Form5291PDFData } from '../../../lib/generateForm5291PDF';
import ForeignCorpDocuments from './ForeignCorpDocuments';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d7cv7kc5q56ra7tufcq0';

const DESIGNATED_AGENT_NAME = 'Bruce Thabit, Esq.';
const DESIGNATED_AGENT_TITLE = 'Director — Serenity Law PLLC';
const DESIGNATED_AGENT_ADDRESS = '1037 NE 65th St #80300, Seattle, WA 98115';

// ── Grantor entity types (per 19 CFR 141.32) ──────────────────────────────
const ENTITY_TYPES = [
  { value: 'individual', label: 'Individual' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'limited_partnership', label: 'Limited Partnership' },
  { value: 'corporation', label: 'Corporation' },
  { value: 'sole_proprietorship', label: 'Sole Proprietorship' },
  { value: 'llc', label: 'Limited Liability Company (LLC)' },
] as const;

type EntityType = typeof ENTITY_TYPES[number]['value'];

// ── U.S. States list ───────────────────────────────────────────────────────
const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
  'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
  'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
  'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire',
  'New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio',
  'Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota',
  'Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia',
  'Wisconsin','Wyoming','District of Columbia',
];

interface Props {
  clientId: string;
  userId?: string;
  clientName: string;
  clientEmail: string;
  company?: string | null;
  phone?: string | null;
  alreadySubmitted?: boolean;
  onSubmitted?: (submittedAt: string) => void;
  importerName?: string | null;
  importerAddress?: string | null;
  importerPhone?: string | null;
}

// ── PDF Preview Modal ──────────────────────────────────────────────────────
interface PreviewModalProps {
  onClose: () => void;
  onGenerate: () => Promise<Uint8Array>;
}

function PDFPreviewModal({ onClose, onGenerate }: PreviewModalProps) {
  const [loading, setLoading] = useState(true);
  const [previewError, setPreviewError] = useState('');
  const [pdfUrl, setPdfUrl] = useState('');
  const urlRef = useRef('');

  useEffect(() => {
    let cancelled = false;
    const generate = async () => {
      try {
        const bytes = await onGenerate();
        if (cancelled) return;
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        urlRef.current = url;
        setPdfUrl(url);
      } catch {
        if (!cancelled) setPreviewError('Could not generate preview. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    generate();
    return () => {
      cancelled = true;
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div
        className="bg-[#0d1520] border-2 border-[#FDB913]/40 rounded-2xl w-full max-w-3xl flex flex-col overflow-hidden"
        style={{ maxHeight: '90vh' }}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-[#FDB913]/20 rounded-lg border border-[#FDB913]/40">
              <i className="ri-file-pdf-line text-[#FDB913] text-lg"></i>
            </div>
            <div>
              <p className="text-white font-black text-sm">PDF Preview — CBP Form 5291</p>
              <p className="text-white/40 text-xs">Review before submitting</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 rounded-lg transition-all cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>
        <div className="flex-1 overflow-hidden min-h-0 bg-gray-200">
          {loading && (
            <div className="flex flex-col items-center justify-center h-64 gap-3">
              <i className="ri-loader-4-line animate-spin text-[#FDB913] text-3xl"></i>
              <p className="text-gray-600 text-sm font-bold">Generating preview...</p>
            </div>
          )}
          {previewError && (
            <div className="flex flex-col items-center justify-center h-64 gap-3">
              <i className="ri-error-warning-line text-red-500 text-3xl"></i>
              <p className="text-red-600 text-sm font-bold">{previewError}</p>
            </div>
          )}
          {pdfUrl && !loading && (
            <iframe
              src={pdfUrl}
              className="w-full h-full"
              style={{ minHeight: '60vh' }}
              title="CBP Form 5291 Preview"
            />
          )}
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-t border-white/10 flex-shrink-0 bg-[#0d1520]">
          <p className="text-white/30 text-xs">
            <i className="ri-information-line mr-1"></i>
            Preview only. Submit the form to generate the final signed PDF.
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white font-bold text-sm rounded-lg transition-all cursor-pointer whitespace-nowrap"
          >
            Close Preview
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Form5291({
  clientId, userId = '', clientName, clientEmail, company, phone,
  alreadySubmitted = false, onSubmitted,
  importerName, importerAddress, importerPhone,
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(alreadySubmitted);
  const [error, setError] = useState('');
  const [lastPdfData, setLastPdfData] = useState<Form5291PDFData | null>(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  // ── Foreign Corporation flag ──────────────────────────────────────────────
  const [isForeignCorp, setIsForeignCorp] = useState<boolean | null>(null);
  const [foreignCorpDocsCount, setForeignCorpDocsCount] = useState(0);

  // ── Exact CBP Form 5291 fields (per 19 CFR 141.32) ───────────────────────

  // SECTION 1: Grantor Identification
  const [entityType, setEntityType] = useState<EntityType>('corporation');
  const [grantorIrsNumber, setGrantorIrsNumber] = useState('');
  const [grantorFullName, setGrantorFullName] = useState(importerName || company || clientName || '');
  const [grantorDba, setGrantorDba] = useState('');
  const [grantorStateOfIncorp, setGrantorStateOfIncorp] = useState('');
  const [grantorAddress, setGrantorAddress] = useState(importerAddress || '');

  // SECTION 2: Appointment — who is being appointed (pre-filled)
  // DESIGNATED_AGENT_NAME, DESIGNATED_AGENT_TITLE, DESIGNATED_AGENT_ADDRESS are constants

  // SECTION 2b: Partnership — list all general partners (19 CFR 141.39)
  const [partnerNames, setPartnerNames] = useState<string[]>(['', '']);

  // SECTION 2c: Limited Partnership — general partners only
  const [limitedPartnerNames, setLimitedPartnerNames] = useState<string[]>(['', '']);

  // SECTION 3: Execution / Signature (was Section 4)
  const [cbpPort, setCbpPort] = useState('');
  const [effectiveDay, setEffectiveDay] = useState('');
  const [effectiveYear, setEffectiveYear] = useState(new Date().getFullYear().toString());
  const [witness1, setWitness1] = useState('');
  const [witness2, setWitness2] = useState('');

  // SECTION 3: Signature fields
  const [sigName, setSigName] = useState('');
  const [signerTitle, setSignerTitle] = useState('');
  const [sigAgreed, setSigAgreed] = useState(false);

  // SECTION 4 (was 5): Corporate Certification — OPTIONAL
  const [certOfficerName, setCertOfficerName] = useState('');
  const [certOfficerCapacity, setCertOfficerCapacity] = useState('');
  const [certBoardDate, setCertBoardDate] = useState('');
  const [certCity, setCertCity] = useState('');
  const [certState, setCertState] = useState('');
  const [certSig, setCertSig] = useState('');

  const buildCurrentPdfData = (): Form5291PDFData => ({
    agentName: DESIGNATED_AGENT_NAME,
    agentTitle: DESIGNATED_AGENT_TITLE,
    agentAddress: DESIGNATED_AGENT_ADDRESS,
    importerName: grantorFullName,
    importerEin: grantorIrsNumber,
    importerPhone: importerPhone || phone || '',
    importerAddress: grantorAddress,
    entityType,
    grantorDba,
    grantorStateOfIncorp,
    partnerNames: partnerNames.filter(Boolean),
    limitedPartnerNames: limitedPartnerNames.filter(Boolean),
    cbpPort,
    effectiveDay,
    effectiveYear,
    effectiveMonth: new Date().toLocaleString('en-US', { month: 'long' }),
    witness1,
    witness2,
    electronicSignature: sigName || clientName,
    signerTitle,
    certOfficerName,
    certOfficerCapacity,
    certBoardDate,
    certCity,
    certState,
    certSig,
    clientName,
    clientEmail,
    submittedAt: new Date().toISOString(),
  });

  // Load saved data when already submitted
  useEffect(() => {
    if (!alreadySubmitted || !clientId) return;
    const loadSavedData = async () => {
      try {
        const { data } = await supabase
          .from('clients')
          .select('name, email, company, importer_of_record_name, importer_of_record_address, importer_of_record_phone, form_5291_submitted_at')
          .eq('id', clientId)
          .maybeSingle();
        const row = (data || {}) as Record<string, unknown>;
        const submittedAt = (row.form_5291_submitted_at as string) || new Date().toISOString();
        setLastPdfData({
          agentName: DESIGNATED_AGENT_NAME,
          agentTitle: DESIGNATED_AGENT_TITLE,
          agentAddress: DESIGNATED_AGENT_ADDRESS,
          importerName: (row.importer_of_record_name as string) || importerName || company || clientName,
          importerEin: '',
          importerPhone: (row.importer_of_record_phone as string) || importerPhone || phone || '',
          importerAddress: (row.importer_of_record_address as string) || importerAddress || '',
          entityType: 'corporation',
          grantorDba: '',
          grantorStateOfIncorp: '',
          cbpPort: '',
          effectiveDay: '',
          effectiveYear: new Date().getFullYear().toString(),
          effectiveMonth: new Date().toLocaleString('en-US', { month: 'long' }),
          witness1: '',
          witness2: '',
          electronicSignature: clientName,
          signerTitle: '',
          clientName,
          clientEmail,
          submittedAt,
        });
      } catch {
        const submittedAt = new Date().toISOString();
        setLastPdfData({
          agentName: DESIGNATED_AGENT_NAME,
          agentTitle: DESIGNATED_AGENT_TITLE,
          agentAddress: DESIGNATED_AGENT_ADDRESS,
          importerName: importerName || company || clientName,
          importerEin: '',
          importerPhone: importerPhone || phone || '',
          importerAddress: importerAddress || '',
          entityType: 'corporation',
          grantorDba: '',
          grantorStateOfIncorp: '',
          cbpPort: '',
          effectiveDay: '',
          effectiveYear: new Date().getFullYear().toString(),
          effectiveMonth: new Date().toLocaleString('en-US', { month: 'long' }),
          witness1: '',
          witness2: '',
          electronicSignature: clientName,
          signerTitle: '',
          clientName,
          clientEmail,
          submittedAt,
        });
      }
    };
    loadSavedData();
  }, [alreadySubmitted, clientId, clientName, clientEmail, company, importerName, importerAddress, importerPhone, phone]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    if (!sigAgreed) { setError('You must agree to the electronic signature certification before submitting.'); return; }
    if (!sigName.trim()) { setError('Please type your full legal name as your electronic signature.'); return; }
    if (!grantorFullName.trim()) { setError('Grantor (Importer of Record) name is required.'); return; }
    if (!grantorAddress.trim()) { setError('Grantor address is required.'); return; }

    const body = new URLSearchParams();
    body.append('form_title', 'CBP Form 5291 - Power of Attorney / Designation of Customs Agent');
    body.append('client_name', clientName);
    body.append('email', clientEmail);
    body.append('company', company || '');
    body.append('submission_date', new Date().toISOString());
    body.append('entity_type', entityType);
    body.append('grantor_irs_number', grantorIrsNumber);
    body.append('grantor_full_name', grantorFullName);
    body.append('grantor_dba', grantorDba);
    body.append('grantor_state_of_incorp', grantorStateOfIncorp);
    body.append('grantor_address', grantorAddress);
    body.append('designated_agent_name', DESIGNATED_AGENT_NAME);
    body.append('designated_agent_title', DESIGNATED_AGENT_TITLE);
    body.append('designated_agent_address', DESIGNATED_AGENT_ADDRESS);
    body.append('cbp_port', cbpPort);
    body.append('effective_day', effectiveDay);
    body.append('effective_year', effectiveYear);
    body.append('witness_1', witness1);
    body.append('witness_2', witness2);
    body.append('electronic_signature', sigName);
    body.append('signer_title', signerTitle);
    body.append('signature_agreed', 'YES');
    if (entityType === 'partnership') {
      body.append('partner_names', partnerNames.filter(Boolean).join('; '));
    }
    if (entityType === 'limited_partnership') {
      body.append('limited_partner_names', limitedPartnerNames.filter(Boolean).join('; '));
    }
    if (entityType === 'corporation') {
      body.append('corp_cert_officer_name', certOfficerName);
      body.append('corp_cert_officer_capacity', certOfficerCapacity);
      body.append('corp_cert_board_date', certBoardDate);
      body.append('corp_cert_city', certCity);
      body.append('corp_cert_state', certState);
      body.append('corp_cert_signature', certSig);
    }

    setSubmitting(true);
    setError('');

    try {
      const res = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (!res.ok) throw new Error('Submission failed. Please try again.');

      const submittedAt = new Date().toISOString();
      await supabase.from('clients')
        .update({ form_5291_submitted_at: submittedAt })
        .eq('id', clientId);

      const captured: Form5291PDFData = {
        ...buildCurrentPdfData(),
        electronicSignature: sigName,
        submittedAt,
      };
      setLastPdfData(captured);
      setSubmitted(true);
      if (onSubmitted) onSubmitted(submittedAt);

      // Notify client
      try {
        await supabase.functions.invoke('send-client-notification', {
          body: {
            eventType: 'form_5291_submitted',
            clientName, clientEmail,
            timestamp: submittedAt,
            portalUrl: 'https://tariffrefund.attorney/portal',
          },
        });
      } catch { /* non-fatal */ }

      // Notify attorney (admin)
      try {
        await supabase.functions.invoke('send-admin-notification', {
          body: {
            eventType: 'form_5291_submitted',
            clientName,
            clientEmail,
            company: company || '',
            submittedAt,
            timestamp: submittedAt,
          },
        });
      } catch { /* non-fatal */ }

      // Insert portal notification for client
      try {
        await supabase.from('client_notifications').insert({
          client_id: clientId,
          title: 'CBP Form 5291 (POA) Submitted',
          message: 'Your Power of Attorney (CBP Form 5291) has been received. Attorney Bruce Thabit, Esq. is now designated as your authorized customs agent with CBP.',
          notification_type: 'form_submitted',
          is_read: false,
        });
      } catch { /* non-fatal */ }
    } catch (err: unknown) {
      setError((err as Error).message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!lastPdfData) return;
    setPdfGenerating(true);
    setPdfError('');
    try {
      const bytes = await generateForm5291PDF(lastPdfData);
      const safeName = clientName.replace(/[^a-z0-9]/gi, '_');
      downloadPDF5291(bytes, `CBP_Form_5291_${safeName}_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      setPdfError('PDF generation failed. Please try again.');
      console.error('Form5291 PDF error:', err);
    } finally {
      setPdfGenerating(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-white/5 backdrop-blur-md border border-green-500/30 rounded-2xl p-8">
        <div className="text-center py-6">
          <div className="w-16 h-16 flex items-center justify-center bg-green-500/20 rounded-full border-2 border-green-500/40 mx-auto mb-4">
            <i className="ri-checkbox-circle-fill text-green-400 text-4xl"></i>
          </div>
          <h3 className="text-2xl font-black text-white mb-2">Form 5291 Submitted</h3>
          <p className="text-white/60 text-sm leading-relaxed max-w-md mx-auto">
            Your Power of Attorney / Designation of Customs Agent has been received. Attorney Bruce Thabit, Esq. is now designated as your customs agent on file.
          </p>
          <p className="text-[#FDB913] text-xs mt-4 font-bold">CBP Form 5291 — Power of Attorney / Designation of Customs Agent</p>
          {lastPdfData ? (
            <div className="mt-6 flex flex-col items-center gap-2">
              <button
                onClick={handleDownloadPDF}
                disabled={pdfGenerating}
                className="flex items-center gap-2 px-6 py-3 bg-[#FDB913] hover:bg-[#e6a800] text-black font-black text-sm rounded-xl transition-all disabled:opacity-50 whitespace-nowrap cursor-pointer"
              >
                {pdfGenerating
                  ? <><i className="ri-loader-4-line animate-spin"></i>Generating PDF...</>
                  : <><i className="ri-file-pdf-line text-lg"></i>Download Signed PDF</>}
              </button>
              {pdfError && <p className="text-red-400 text-xs flex items-center gap-1"><i className="ri-error-warning-line"></i>{pdfError}</p>}
            </div>
          ) : (
            <div className="mt-6 flex items-center justify-center gap-2">
              <i className="ri-loader-4-line animate-spin text-white/30 text-sm"></i>
              <p className="text-white/30 text-xs">Loading PDF data...</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  const currentMonth = new Date().toLocaleString('en-US', { month: 'long' });

  return (
    <>
      {showPreview && (
        <PDFPreviewModal
          onClose={() => setShowPreview(false)}
          onGenerate={() => generateForm5291PDF(buildCurrentPdfData())}
        />
      )}

      <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl overflow-hidden">
        {/* Header toggle */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between p-6 hover:bg-white/5 transition-colors cursor-pointer text-left"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-[#FDB913]/20 rounded-xl border border-[#FDB913]/40 flex-shrink-0">
              <i className="ri-user-star-line text-[#FDB913] text-2xl"></i>
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-lg font-black text-white">CBP Form 5291</h3>
                <span className="bg-[#FDB913]/20 border border-[#FDB913]/40 text-[#FDB913] text-xs font-black px-2 py-0.5 rounded-full whitespace-nowrap">
                  Action Required
                </span>
              </div>
              <p className="text-white/50 text-xs mt-0.5">Power of Attorney — Appoint Attorney Thabit as your CBP Customs Agent</p>
            </div>
          </div>
          <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
            <i className={`${expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-white/60 text-xl`}></i>
          </div>
        </button>

        {expanded && (
          <div className="border-t border-white/10 p-6">
            {/* Info banner */}
            <div className="bg-gradient-to-r from-[#FDB913]/10 to-[#FDB913]/5 border border-[#FDB913]/30 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-[#FDB913]/20 rounded-lg border border-[#FDB913]/40 flex-shrink-0">
                  <i className="ri-information-line text-[#FDB913] text-lg"></i>
                </div>
                <div>
                  <p className="text-[#FDB913] font-black text-sm mb-1">CBP Form 5291 — Power of Attorney (19 CFR 141.32)</p>
                  <p className="text-white/60 text-xs leading-relaxed">
                    This form officially designates <strong className="text-white">Bruce Thabit, Esq. — Serenity Law PLLC</strong> as your
                    authorized customs agent with U.S. Customs and Border Protection. Agent fields are pre-filled.
                    Please complete your grantor identification, address, and sign below.
                  </p>
                </div>
              </div>
            </div>

            {/* ── FOREIGN CORPORATION QUESTION ── */}
            <div className="mb-6 border-2 border-white/15 rounded-xl overflow-hidden">
              <div className="bg-[#0d1520] px-5 py-3 border-b border-white/10">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 flex items-center justify-center bg-orange-500/20 border border-orange-500/40 rounded-lg flex-shrink-0">
                    <i className="ri-earth-line text-orange-400 text-sm"></i>
                  </div>
                  <p className="text-white font-black text-sm">
                    Is your corporation / entity a <span className="text-orange-400">foreign</span> corporation that has not qualified to conduct business under U.S. law?
                  </p>
                </div>
                <p className="text-white/40 text-xs mt-1.5 ml-9 leading-relaxed">
                  Per 19 CFR 141.32(b), foreign corporations must provide additional supporting documentation with the POA.
                </p>
              </div>
              <div className="flex bg-[#080f18]">
                <button
                  type="button"
                  onClick={() => setIsForeignCorp(false)}
                  className={`flex-1 flex items-center justify-center gap-2 py-3.5 font-black text-sm transition-all cursor-pointer whitespace-nowrap border-r border-white/10 ${
                    isForeignCorp === false
                      ? 'bg-green-500/20 text-green-400 border-b-2 border-b-green-500'
                      : 'text-white/50 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <i className={`${isForeignCorp === false ? 'ri-checkbox-circle-fill' : 'ri-checkbox-blank-circle-line'} text-base`}></i>
                  No — U.S. Domestic Entity
                </button>
                <button
                  type="button"
                  onClick={() => setIsForeignCorp(true)}
                  className={`flex-1 flex items-center justify-center gap-2 py-3.5 font-black text-sm transition-all cursor-pointer whitespace-nowrap ${
                    isForeignCorp === true
                      ? 'bg-orange-500/20 text-orange-400 border-b-2 border-b-orange-500'
                      : 'text-white/50 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <i className={`${isForeignCorp === true ? 'ri-checkbox-circle-fill' : 'ri-checkbox-blank-circle-line'} text-base`}></i>
                  Yes — Foreign Corporation / Entity
                </button>
              </div>
            </div>

            {/* ── FOREIGN CORP SUPPORTING DOCS ── */}
            {isForeignCorp === true && (
              <div className="mb-6">
                <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl px-4 py-3 mb-4">
                  <div className="flex items-start gap-2">
                    <i className="ri-alert-line text-orange-400 text-sm flex-shrink-0 mt-0.5"></i>
                    <div>
                      <p className="text-orange-400 font-black text-sm">Additional Documents Required Before Submitting</p>
                      <p className="text-white/55 text-xs mt-1 leading-relaxed">
                        As a foreign corporation/entity that has not qualified to conduct business under U.S. law,
                        CBP requires documentation establishing the authority of the grantor to execute this POA on behalf of the corporation.
                        Please upload the required documents below, then complete and submit the POA form.
                      </p>
                    </div>
                  </div>
                </div>
                <ForeignCorpDocuments
                  clientId={clientId}
                  userId={userId}
                  onAllRequiredUploaded={setForeignCorpDocsCount}
                />
                {foreignCorpDocsCount > 0 && (
                  <div className="mt-3 flex items-center gap-2 bg-green-500/10 border border-green-500/25 rounded-lg px-3 py-2">
                    <i className="ri-checkbox-circle-fill text-green-400 text-sm"></i>
                    <p className="text-green-400 text-xs font-bold">{foreignCorpDocsCount} supporting document{foreignCorpDocsCount !== 1 ? 's' : ''} uploaded — you may now submit the POA.</p>
                  </div>
                )}
              </div>
            )}

            {isForeignCorp === null && (
              <div className="mb-4 flex items-center gap-2 bg-[#FDB913]/10 border border-[#FDB913]/25 rounded-lg px-3 py-2.5">
                <i className="ri-information-line text-[#FDB913] text-sm"></i>
                <p className="text-[#FDB913] text-xs font-bold">Please answer the foreign corporation question above before filling the form.</p>
              </div>
            )}

            <form
              data-readdy-form
              id={`cbp-form-5291-${clientId}`}
              onSubmit={handleSubmit}
            >
              {/* ── OFFICIAL FORM REPLICA ── */}
              <div className="border-2 border-[#1a2a3a] rounded-xl overflow-hidden font-sans bg-white">

                {/* ── FORM HEADER ── */}
                <div className="bg-white border-b-2 border-gray-400 px-5 py-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center border-2 border-gray-400 rounded-full bg-gray-100">
                          <i className="ri-shield-star-line text-gray-500 text-xl"></i>
                        </div>
                        <div>
                          <p className="text-gray-700 text-[10px] font-bold uppercase tracking-wide">Department of Homeland Security</p>
                          <p className="text-gray-900 text-sm font-black">U.S. Customs and Border Protection</p>
                          <p className="text-gray-900 font-black text-lg mt-1 tracking-wide uppercase">Power of Attorney</p>
                          <p className="text-gray-600 text-xs">Designation of Customs Agent — 19 CFR 141.32</p>
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 border border-gray-400 p-2 rounded">
                      <p className="text-gray-900 font-black text-sm">CBP Form 5291</p>
                      <p className="text-gray-500 text-[9px] mt-0.5">OMB No. 1651-0034</p>
                    </div>
                  </div>
                  {/* KNOW ALL MEN preamble */}
                  <div className="mt-3 bg-gray-50 border border-gray-300 rounded p-2">
                    <p className="text-gray-700 text-[10px] leading-relaxed italic" style={{ fontFamily: 'Georgia, serif' }}>
                      <strong className="not-italic">KNOW ALL MEN BY THESE PRESENTS:</strong> That the undersigned, as Importer of Record, hereby constitutes and appoints{' '}
                      <strong className="not-italic">{DESIGNATED_AGENT_NAME}</strong>, {DESIGNATED_AGENT_ADDRESS}, as true and lawful agent and attorney of the{' '}
                      principal named below with full power and authority to do and perform every lawful act and thing the said agent and attorney may deem requisite and necessary
                      to be done for and on behalf of the said principal without limitation of any kind as fully as said principal could do if present and acting, and hereby ratify
                      and confirm all that said agent and attorney shall lawfully do or cause to be done by virtue of these presents until notice of revocation in writing is duly
                      given before that date, however, if the donor of this power of attorney is a partnership, the said power shall in no case have any force or effect after the
                      expiration of 2 years. This power of attorney appoints specifically authorizes the agent to act thereunder at all Custom Centers and at all CBP ports including
                      processing IEEPA refunds in the CAPE system.
                    </p>
                  </div>
                </div>

                {/* ── SECTION 1: GRANTOR IDENTIFICATION ── */}
                <div className="border-b-2 border-gray-400">
                  <div className="bg-gray-200 px-4 py-1.5 border-b border-gray-400">
                    <p className="text-gray-900 text-[10px] font-black uppercase tracking-wider">
                      Section 1 — Grantor Identification (Importer of Record)
                    </p>
                  </div>
                  <div className="bg-white px-4 py-3 space-y-3">
                    {/* Entity type checkboxes */}
                    <div>
                      <div className="bg-gray-100 px-3 py-1 border border-gray-300 border-b-0 rounded-t">
                        <p className="text-gray-600 text-[9px] font-bold uppercase">Check Appropriate Box — Type of Entity <span className="text-red-600">*</span></p>
                      </div>
                      <div className="border border-gray-300 rounded-b p-2 flex flex-wrap gap-2">
                        {ENTITY_TYPES.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setEntityType(opt.value)}
                            className={`flex items-center gap-2 px-3 py-1.5 border text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                              entityType === opt.value
                                ? 'border-gray-900 bg-gray-900 text-white'
                                : 'border-gray-300 bg-white text-gray-700 hover:border-gray-500'
                            }`}
                          >
                            <div className={`w-3.5 h-3.5 border-2 flex items-center justify-center flex-shrink-0 ${
                              entityType === opt.value ? 'border-white bg-white' : 'border-gray-500 bg-white'
                            }`}>
                              {entityType === opt.value && <i className="ri-check-line text-gray-900 text-[8px]"></i>}
                            </div>
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* IRS / SSN Number */}
                    <div className="border border-gray-300">
                      <div className="bg-gray-100 px-3 py-1 border-b border-gray-300">
                        <p className="text-gray-600 text-[9px] font-bold uppercase">
                          IRS or SSN Number <span className="text-gray-400 normal-case font-normal">(Federal Tax ID / EIN)</span>
                        </p>
                      </div>
                      <input
                        type="text"
                        maxLength={50}
                        value={grantorIrsNumber}
                        onChange={(e) => setGrantorIrsNumber(e.target.value)}
                        className="w-full px-3 py-2.5 bg-white text-gray-900 text-sm focus:outline-none focus:bg-[#fffbf0] transition-colors"
                        placeholder="XX-XXXXXXX (EIN) or XXX-XX-XXXX (SSN)"
                      />
                    </div>

                    {/* Full Legal Name */}
                    <div className="border border-gray-300">
                      <div className="bg-gray-100 px-3 py-1 border-b border-gray-300">
                        <p className="text-gray-600 text-[9px] font-bold uppercase">
                          Full Legal Name of Grantor (Importer of Record) <span className="text-red-600">*</span>
                          <span className="text-gray-400 normal-case font-normal ml-1">— must match Articles of Incorporation / legal registration</span>
                        </p>
                      </div>
                      <input
                        type="text"
                        required
                        maxLength={200}
                        value={grantorFullName}
                        onChange={(e) => setGrantorFullName(e.target.value)}
                        className="w-full px-3 py-2.5 bg-white text-gray-900 text-sm focus:outline-none focus:bg-[#fffbf0] transition-colors"
                        placeholder="Full legal name of the Importer of Record"
                      />
                    </div>

                    {/* DBA + State of Incorp */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="border border-gray-300">
                        <div className="bg-gray-100 px-3 py-1 border-b border-gray-300">
                          <p className="text-gray-600 text-[9px] font-bold uppercase">
                            Doing Business As (DBA) <span className="text-gray-400 normal-case font-normal">(if applicable)</span>
                          </p>
                        </div>
                        <input
                          type="text"
                          maxLength={200}
                          value={grantorDba}
                          onChange={(e) => setGrantorDba(e.target.value)}
                          className="w-full px-3 py-2.5 bg-white text-gray-900 text-sm focus:outline-none focus:bg-[#fffbf0] transition-colors"
                          placeholder="Trade name or DBA"
                        />
                      </div>
                      <div className="border border-gray-300">
                        <div className="bg-gray-100 px-3 py-1 border-b border-gray-300">
                          <p className="text-gray-600 text-[9px] font-bold uppercase">
                            Under the Laws of the State of <span className="text-gray-400 normal-case font-normal">(if corp/LLC)</span>
                          </p>
                        </div>
                        <select
                          value={grantorStateOfIncorp}
                          onChange={(e) => setGrantorStateOfIncorp(e.target.value)}
                          className="w-full px-3 py-2.5 bg-white text-gray-900 text-sm focus:outline-none focus:bg-[#fffbf0] transition-colors cursor-pointer"
                        >
                          <option value="">— Select State —</option>
                          {US_STATES.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                          <option value="Foreign">Foreign / Non-U.S.</option>
                        </select>
                      </div>
                    </div>

                    {/* Partnership — list all general partners (19 CFR 141.39) */}
                    {entityType === 'partnership' && (
                      <div className="border-2 border-amber-400/60 rounded-lg overflow-hidden">
                        <div className="bg-amber-50 px-3 py-2 border-b border-amber-300 flex items-center justify-between">
                          <div>
                            <p className="text-amber-800 text-[9px] font-black uppercase tracking-wider">
                              Partnership — Names of All General Partners <span className="text-red-600">*</span>
                            </p>
                            <p className="text-amber-700 text-[8px] mt-0.5">
                              Per 19 CFR 141.39 — all general partners must be listed. Partnership POAs are valid for a maximum of <strong>2 years</strong> from execution date.
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setPartnerNames([...partnerNames, ''])}
                            className="flex items-center gap-1 px-2 py-1 bg-amber-200 hover:bg-amber-300 text-amber-900 text-[9px] font-black rounded transition-all cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-add-line text-xs"></i>Add Partner
                          </button>
                        </div>
                        <div className="bg-white p-3 space-y-2">
                          {partnerNames.map((name, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <span className="text-gray-400 text-[9px] font-bold w-16 flex-shrink-0">Partner {idx + 1}</span>
                              <input
                                type="text"
                                maxLength={200}
                                value={name}
                                onChange={(e) => {
                                  const updated = [...partnerNames];
                                  updated[idx] = e.target.value;
                                  setPartnerNames(updated);
                                }}
                                className="flex-1 border border-gray-300 px-3 py-1.5 text-gray-900 text-sm focus:outline-none focus:bg-[#fffbf0] transition-colors"
                                placeholder={`Full legal name of General Partner ${idx + 1}`}
                              />
                              {partnerNames.length > 2 && (
                                <button
                                  type="button"
                                  onClick={() => setPartnerNames(partnerNames.filter((_, i) => i !== idx))}
                                  className="w-6 h-6 flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-all cursor-pointer flex-shrink-0"
                                >
                                  <i className="ri-close-line text-xs"></i>
                                </button>
                              )}
                            </div>
                          ))}
                          <div className="mt-2 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                            <i className="ri-time-line text-amber-600 text-xs flex-shrink-0 mt-0.5"></i>
                            <p className="text-amber-700 text-[9px] leading-relaxed">
                              <strong>2-Year Validity Notice:</strong> Per 19 CFR 141.39, this Power of Attorney granted by a partnership is valid for a maximum of two (2) years from the date of execution. A new Form 5291 must be filed upon expiration or any change in partnership membership.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Address */}
                    <div className="border border-gray-300">
                      <div className="bg-gray-100 px-3 py-1 border-b border-gray-300">
                        <p className="text-gray-600 text-[9px] font-bold uppercase">
                          Residing at / Having an Office and Place of Business at <span className="text-red-600">*</span>
                          <span className="text-gray-400 normal-case font-normal ml-1">(No P.O. Boxes)</span>
                        </p>
                      </div>
                      <input
                        type="text"
                        required
                        maxLength={300}
                        value={grantorAddress}
                        onChange={(e) => setGrantorAddress(e.target.value)}
                        className="w-full px-3 py-2.5 bg-white text-gray-900 text-sm focus:outline-none focus:bg-[#fffbf0] transition-colors"
                        placeholder="Street address, City, State, ZIP (no P.O. Boxes)"
                      />
                    </div>
                  </div>
                </div>

                {/* ── SECTION 2: APPOINTMENT (Pre-filled) ── */}
                <div className="border-b-2 border-gray-400">
                  <div className="bg-gray-200 px-4 py-1.5 border-b border-gray-400">
                    <p className="text-gray-900 text-[10px] font-black uppercase tracking-wider">
                      Section 2 — Appointment of Agent
                    </p>
                  </div>
                  <div className="bg-white">
                    <div className="grid grid-cols-12 border-b border-gray-300">
                      <div className="col-span-5 border-r border-gray-300">
                        <div className="bg-gray-100 px-2 py-1 border-b border-gray-300">
                          <p className="text-gray-600 text-[9px] font-bold uppercase">Hereby Constitutes and Appoints</p>
                        </div>
                        <div className="px-3 py-2 bg-[#fffbf0]">
                          <p className="text-gray-900 font-black text-sm">{DESIGNATED_AGENT_NAME}</p>
                          <p className="text-gray-600 text-xs">{DESIGNATED_AGENT_TITLE}</p>
                        </div>
                      </div>
                      <div className="col-span-7">
                        <div className="bg-gray-100 px-2 py-1 border-b border-gray-300">
                          <p className="text-gray-600 text-[9px] font-bold uppercase">Agent Address</p>
                        </div>
                        <div className="px-3 py-2 bg-[#fffbf0]">
                          <p className="text-gray-900 text-xs font-bold">{DESIGNATED_AGENT_ADDRESS}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── SECTION 3: EXECUTION / SIGNATURE (was Section 4) ── */}
                <div className="border-b border-gray-300">
                  <div className="bg-gray-200 px-4 py-1.5 border-b border-gray-400">
                    <p className="text-gray-900 text-[10px] font-black uppercase tracking-wider">
                      Section 3 — Execution &amp; Certification (Signature, Capacity, Date)
                    </p>
                  </div>
                  <div className="bg-white px-4 py-3 space-y-3">
                    {/* ESIGN disclosure */}
                    <div className="bg-gray-50 border border-gray-300 p-3">
                      <p className="text-gray-600 text-[9px] leading-relaxed">
                        <strong className="text-gray-800">Electronic Signature Disclosure (ESIGN Act, 15 U.S.C. § 7001):</strong>{' '}
                        By typing your name below, you acknowledge this constitutes your legally binding electronic signature.
                        You designate <strong className="text-gray-800">Bruce Thabit, Esq. — Serenity Law PLLC</strong> as your authorized customs agent with CBP.
                        False statements may subject you to criminal and civil penalties (18 U.S.C. § 1001).
                      </p>
                    </div>

                    {/* Signature + Date */}
                    <div className="grid grid-cols-12 gap-0 border border-gray-300">
                      <div className="col-span-8 border-r border-gray-300">
                        <div className="bg-gray-100 px-3 py-1 border-b border-gray-300">
                          <p className="text-gray-600 text-[9px] font-bold uppercase">
                            Signature (Type Full Legal Name) <span className="text-red-600">*</span>
                          </p>
                        </div>
                        <input
                          type="text"
                          required
                          maxLength={200}
                          value={sigName}
                          onChange={(e) => setSigName(e.target.value)}
                          className="w-full px-3 py-3 bg-white text-gray-900 text-base focus:outline-none focus:bg-[#fffbf0] transition-colors"
                          placeholder="Type your full legal name..."
                          style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}
                        />
                      </div>
                      <div className="col-span-4">
                        <div className="bg-gray-100 px-3 py-1 border-b border-gray-300">
                          <p className="text-gray-600 text-[9px] font-bold uppercase">Date</p>
                        </div>
                        <div className="px-3 py-3 bg-[#fffbf0]">
                          <p className="text-gray-900 text-sm font-bold">
                            {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Capacity / Title */}
                    <div className="border border-gray-300">
                      <div className="bg-gray-100 px-3 py-1 border-b border-gray-300">
                        <p className="text-gray-600 text-[9px] font-bold uppercase">
                          Capacity / Title <span className="text-gray-400 normal-case font-normal">(e.g. President, Partner, Member, Owner)</span>
                        </p>
                      </div>
                      <input
                        type="text"
                        maxLength={100}
                        value={signerTitle}
                        onChange={(e) => setSignerTitle(e.target.value)}
                        className="w-full px-3 py-2.5 bg-white text-gray-900 text-sm focus:outline-none focus:bg-[#fffbf0] transition-colors"
                        placeholder="e.g. President, CEO, Owner, Partner, Member..."
                      />
                    </div>

                    {/* Certification checkbox */}
                    <button
                      type="button"
                      onClick={() => setSigAgreed(!sigAgreed)}
                      className={`w-full flex items-start gap-3 p-3 border-2 text-left transition-all cursor-pointer ${
                        sigAgreed ? 'bg-green-50 border-green-500' : 'bg-gray-50 border-gray-300 hover:border-gray-500'
                      }`}
                    >
                      <div className={`w-5 h-5 flex-shrink-0 border-2 flex items-center justify-center mt-0.5 transition-all ${
                        sigAgreed ? 'bg-green-500 border-green-500' : 'border-gray-400 bg-white'
                      }`}>
                        {sigAgreed && <i className="ri-check-line text-white text-xs"></i>}
                      </div>
                      <p className="text-gray-600 text-[10px] leading-relaxed">
                        I certify that the information provided is true and accurate. I hereby designate{' '}
                        <strong className="text-gray-800">Bruce Thabit, Esq. — Serenity Law PLLC</strong> as my authorized customs agent
                        with U.S. Customs and Border Protection. This constitutes a legally binding electronic signature under the ESIGN Act (15 U.S.C. § 7001).
                      </p>
                    </button>
                  </div>
                </div>

                {/* ── SECTION 4 (was 5): CORPORATE CERTIFICATION — OPTIONAL ── */}
                {entityType === 'corporation' && (
                  <div className="border-b border-gray-300">
                    <div className="bg-gray-200 px-4 py-1.5 border-b border-gray-400">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <p className="text-gray-900 text-[10px] font-black uppercase tracking-wider">
                          Section 4 — Corporate Certification
                          <span className="text-gray-500 normal-case font-normal ml-1 text-[9px]">(Second corporate officer — if applicable)</span>
                        </p>
                        <span className="bg-gray-300 text-gray-600 text-[8px] font-black px-2 py-0.5 rounded uppercase tracking-wide">Optional</span>
                      </div>
                    </div>
                    <div className="bg-white px-4 py-3 space-y-3">
                      {/* Info note */}
                      <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded px-3 py-2.5">
                        <i className="ri-information-line text-blue-500 text-sm flex-shrink-0 mt-0.5"></i>
                        <p className="text-blue-700 text-[9px] leading-relaxed">
                          <strong>Corporate Certification is not required.</strong> Some corporations choose to include a second officer certification to further evidence the authority of the signing officer. This section is entirely optional — leave it blank if not needed.
                        </p>
                      </div>

                      {/* Certification preamble */}
                      <div className="bg-gray-50 border border-gray-300 p-3">
                        <p className="text-gray-700 text-[9px] leading-relaxed italic" style={{ fontFamily: 'Georgia, serif' }}>
                          I, <strong className={`not-italic ${certOfficerName ? 'text-gray-900' : 'text-gray-400'}`}>{certOfficerName || '(Second Officer Name)'}</strong>,
                          certify that I am the <strong className={`not-italic ${certOfficerCapacity ? 'text-gray-900' : 'text-gray-400'}`}>{certOfficerCapacity || '(Capacity/Title)'}</strong> of{' '}
                          <strong className={`not-italic ${grantorFullName ? 'text-gray-900' : 'text-gray-400'}`}>{grantorFullName || '(Organization Name)'}</strong>,
                          organized under the laws of the State of <strong className={`not-italic ${grantorStateOfIncorp ? 'text-gray-900' : 'text-gray-400'}`}>{grantorStateOfIncorp || '(State)'}</strong>;
                          that <strong className={`not-italic ${sigName ? 'text-gray-900' : 'text-gray-400'}`}>{sigName || '(First Officer Name)'}</strong> is the{' '}
                          <strong className={`not-italic ${signerTitle ? 'text-gray-900' : 'text-gray-400'}`}>{signerTitle || '(Title of First Officer)'}</strong> of said corporation,
                          and that the Board of Directors of said corporation, at a meeting duly held on{' '}
                          <strong className={`not-italic ${certBoardDate ? 'text-gray-900' : 'text-gray-400'}`}>{certBoardDate || '(Board Meeting Date)'}</strong>,
                          adopted a resolution authorizing the execution of powers of attorney on behalf of the corporation.
                        </p>
                      </div>

                      {/* Second officer name + capacity */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="border border-gray-300">
                          <div className="bg-gray-100 px-3 py-1 border-b border-gray-300">
                            <p className="text-gray-600 text-[9px] font-bold uppercase">
                              Second Officer Full Name
                              <span className="text-gray-400 normal-case font-normal ml-1">(not the signer above)</span>
                            </p>
                          </div>
                          <input
                            type="text"
                            maxLength={200}
                            value={certOfficerName}
                            onChange={(e) => setCertOfficerName(e.target.value)}
                            className="w-full px-3 py-2.5 bg-white text-gray-900 text-sm focus:outline-none focus:bg-[#fffbf0] transition-colors"
                            placeholder="e.g. Jane Smith"
                          />
                        </div>
                        <div className="border border-gray-300">
                          <div className="bg-gray-100 px-3 py-1 border-b border-gray-300">
                            <p className="text-gray-600 text-[9px] font-bold uppercase">
                              Capacity / Title of Second Officer
                            </p>
                          </div>
                          <input
                            type="text"
                            maxLength={100}
                            value={certOfficerCapacity}
                            onChange={(e) => setCertOfficerCapacity(e.target.value)}
                            className="w-full px-3 py-2.5 bg-white text-gray-900 text-sm focus:outline-none focus:bg-[#fffbf0] transition-colors"
                            placeholder="e.g. Secretary, Vice President, CFO"
                          />
                        </div>
                      </div>

                      {/* Board meeting date + city/state */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="border border-gray-300">
                          <div className="bg-gray-100 px-3 py-1 border-b border-gray-300">
                            <p className="text-gray-600 text-[9px] font-bold uppercase">Board Resolution Date</p>
                          </div>
                          <input
                            type="date"
                            value={certBoardDate}
                            onChange={(e) => setCertBoardDate(e.target.value)}
                            className="w-full px-3 py-2.5 bg-white text-gray-900 text-sm focus:outline-none focus:bg-[#fffbf0] transition-colors cursor-pointer"
                          />
                        </div>
                        <div className="border border-gray-300">
                          <div className="bg-gray-100 px-3 py-1 border-b border-gray-300">
                            <p className="text-gray-600 text-[9px] font-bold uppercase">City</p>
                          </div>
                          <input
                            type="text"
                            maxLength={100}
                            value={certCity}
                            onChange={(e) => setCertCity(e.target.value)}
                            className="w-full px-3 py-2.5 bg-white text-gray-900 text-sm focus:outline-none focus:bg-[#fffbf0] transition-colors"
                            placeholder="City"
                          />
                        </div>
                        <div className="border border-gray-300">
                          <div className="bg-gray-100 px-3 py-1 border-b border-gray-300">
                            <p className="text-gray-600 text-[9px] font-bold uppercase">State</p>
                          </div>
                          <select
                            value={certState}
                            onChange={(e) => setCertState(e.target.value)}
                            className="w-full px-3 py-2.5 bg-white text-gray-900 text-sm focus:outline-none focus:bg-[#fffbf0] transition-colors cursor-pointer"
                          >
                            <option value="">— State —</option>
                            {US_STATES.map((s) => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Second officer signature */}
                      <div className="border border-gray-300">
                        <div className="bg-gray-100 px-3 py-1 border-b border-gray-300">
                          <p className="text-gray-600 text-[9px] font-bold uppercase">
                            Second Officer Electronic Signature
                            <span className="text-gray-400 normal-case font-normal ml-1">(type full legal name)</span>
                          </p>
                        </div>
                        <input
                          type="text"
                          maxLength={200}
                          value={certSig}
                          onChange={(e) => setCertSig(e.target.value)}
                          className="w-full px-3 py-3 bg-white text-gray-900 text-base focus:outline-none focus:bg-[#fffbf0] transition-colors"
                          placeholder="Type second officer's full legal name..."
                          style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* ── SECTION 5: PARTNERSHIP CERTIFICATE — OPTIONAL ── */}
                <div className="border-b border-gray-300">
                    <div className="bg-gray-200 px-4 py-1.5 border-b border-gray-400">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <p className="text-gray-900 text-[10px] font-black uppercase tracking-wider">
                          Section 5 — Partnership Certificate
                        </p>
                        <span className="bg-amber-200 text-amber-800 text-[8px] font-black px-2 py-0.5 rounded uppercase tracking-wide">Not Required — Partnerships Only</span>
                      </div>
                    </div>
                    <div className="bg-white px-4 py-3 space-y-3">
                      {/* Rule note */}
                      <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded px-3 py-2.5">
                        <i className="ri-information-line text-amber-600 text-sm flex-shrink-0 mt-0.5"></i>
                        <p className="text-amber-800 text-[9px] leading-relaxed">
                          <strong>Rule:</strong> A power of attorney granted by a partnership shall state the names of all members of the partnership.
                          One member of the partnership may execute a power of attorney in the name of the partnership for the transaction of all its Customs business.
                          A copy of the partnership agreement must accompany the power of attorney.
                          <span className="block mt-1 text-amber-700 font-bold">This section is not required — it only applies to Partnerships.</span>
                        </p>
                      </div>

                      {/* Partner list */}
                      <div className="border border-gray-300 rounded overflow-hidden">
                        <div className="bg-gray-100 px-3 py-1.5 border-b border-gray-300 flex items-center justify-between">
                          <p className="text-gray-600 text-[9px] font-bold uppercase">Names of All Members of the Partnership</p>
                          <button
                            type="button"
                            onClick={() => setPartnerNames([...partnerNames, ''])}
                            className="flex items-center gap-1 px-2 py-0.5 bg-amber-200 hover:bg-amber-300 text-amber-900 text-[9px] font-black rounded transition-all cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-add-line text-xs"></i>Add Partner
                          </button>
                        </div>
                        <div className="bg-white p-3 space-y-2">
                          {partnerNames.map((name, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <span className="text-gray-400 text-[9px] font-bold w-16 flex-shrink-0">Partner {idx + 1}</span>
                              <input
                                type="text"
                                maxLength={200}
                                value={name}
                                onChange={(e) => {
                                  const updated = [...partnerNames];
                                  updated[idx] = e.target.value;
                                  setPartnerNames(updated);
                                }}
                                className="flex-1 border border-gray-300 px-3 py-1.5 text-gray-900 text-sm focus:outline-none focus:bg-[#fffbf0] transition-colors"
                                placeholder={`Full legal name of Partner ${idx + 1}`}
                              />
                              {partnerNames.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => setPartnerNames(partnerNames.filter((_, i) => i !== idx))}
                                  className="w-6 h-6 flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-all cursor-pointer flex-shrink-0"
                                >
                                  <i className="ri-close-line text-xs"></i>
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="flex items-start gap-2 bg-gray-50 border border-gray-300 rounded px-3 py-2">
                        <i className="ri-file-copy-line text-gray-500 text-xs flex-shrink-0 mt-0.5"></i>
                        <p className="text-gray-500 text-[9px] leading-relaxed">
                          A copy of the partnership agreement must accompany this power of attorney when submitted to CBP.
                        </p>
                      </div>
                    </div>
                  </div>


                {/* ── SECTION 6: LIMITED PARTNERSHIP CERTIFICATE — OPTIONAL ── */}
                <div className="border-b border-gray-300">
                    <div className="bg-gray-200 px-4 py-1.5 border-b border-gray-400">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <p className="text-gray-900 text-[10px] font-black uppercase tracking-wider">
                          Section 6 — Limited Partnership Certificate
                        </p>
                        <span className="bg-amber-200 text-amber-800 text-[8px] font-black px-2 py-0.5 rounded uppercase tracking-wide">Not Required — Limited Partnerships Only</span>
                      </div>
                    </div>
                    <div className="bg-white px-4 py-3 space-y-3">
                      {/* Rule note */}
                      <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded px-3 py-2.5">
                        <i className="ri-information-line text-amber-600 text-sm flex-shrink-0 mt-0.5"></i>
                        <p className="text-amber-800 text-[9px] leading-relaxed">
                          <strong>Rule:</strong> A power of attorney granted by a limited partnership need only state the names of the general partners who have authority to bind the firm
                          unless the partnership agreement provides otherwise. A copy of the partnership agreement must accompany the power of attorney.
                          <span className="block mt-1 text-amber-700 font-bold">This section is not required — it only applies to Limited Partnerships.</span>
                        </p>
                      </div>

                      {/* General partner list */}
                      <div className="border border-gray-300 rounded overflow-hidden">
                        <div className="bg-gray-100 px-3 py-1.5 border-b border-gray-300 flex items-center justify-between">
                          <p className="text-gray-600 text-[9px] font-bold uppercase">Names of General Partners with Authority to Bind the Firm</p>
                          <button
                            type="button"
                            onClick={() => setLimitedPartnerNames([...limitedPartnerNames, ''])}
                            className="flex items-center gap-1 px-2 py-0.5 bg-amber-200 hover:bg-amber-300 text-amber-900 text-[9px] font-black rounded transition-all cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-add-line text-xs"></i>Add Partner
                          </button>
                        </div>
                        <div className="bg-white p-3 space-y-2">
                          {limitedPartnerNames.map((name, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <span className="text-gray-400 text-[9px] font-bold w-24 flex-shrink-0">Gen. Partner {idx + 1}</span>
                              <input
                                type="text"
                                maxLength={200}
                                value={name}
                                onChange={(e) => {
                                  const updated = [...limitedPartnerNames];
                                  updated[idx] = e.target.value;
                                  setLimitedPartnerNames(updated);
                                }}
                                className="flex-1 border border-gray-300 px-3 py-1.5 text-gray-900 text-sm focus:outline-none focus:bg-[#fffbf0] transition-colors"
                                placeholder={`Full legal name of General Partner ${idx + 1}`}
                              />
                              {limitedPartnerNames.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => setLimitedPartnerNames(limitedPartnerNames.filter((_, i) => i !== idx))}
                                  className="w-6 h-6 flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-all cursor-pointer flex-shrink-0"
                                >
                                  <i className="ri-close-line text-xs"></i>
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="flex items-start gap-2 bg-gray-50 border border-gray-300 rounded px-3 py-2">
                        <i className="ri-file-copy-line text-gray-500 text-xs flex-shrink-0 mt-0.5"></i>
                        <p className="text-gray-500 text-[9px] leading-relaxed">
                          A copy of the partnership agreement must accompany this power of attorney when submitted to CBP.
                        </p>
                      </div>
                    </div>
                  </div>

                {/* Form Footer */}
                <div className="bg-gray-100 px-4 py-2 border-t border-gray-400">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <p className="text-gray-500 text-[9px]">CBP Form 5291 — Power of Attorney — OMB No. 1651-0034 — 19 CFR 141.32</p>
                    <p className="text-gray-500 text-[9px]">ESIGN Act Compliant Electronic Record</p>
                  </div>
                </div>
              </div>
              {/* ── END FORM REPLICA ── */}

              {error && (
                <div className="mt-4 flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3">
                  <i className="ri-error-warning-line text-red-400"></i>
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              {/* Action buttons */}
              <div className="mt-5 flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => setShowPreview(true)}
                  className="flex-1 py-3.5 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-black text-sm rounded-xl transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                >
                  <i className="ri-eye-line text-lg"></i>Preview PDF
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 py-3.5 bg-[#FDB913] hover:bg-[#e6a800] text-black font-black text-sm rounded-xl transition-all disabled:opacity-50 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <><i className="ri-loader-4-line animate-spin"></i>Submitting...</>
                  ) : (
                    <><i className="ri-send-plane-fill"></i>Submit CBP Form 5291</>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </>
  );
}
